/**
 * TrustLayer — "why the number holds", in the exhibit system. Three top-ruled
 * cells (no icon chips), numbered 01–03, matching the security + how-it-works
 * molecule. The attestation sign-off chain is a formalized ledger. Atoms:
 * eyebrow only.
 */

import { SectionHeader } from './sectionPrimitives';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;

interface ChainStep {
  role: string;
  action: string;
}

interface TrustColumn {
  title: string;
  body: string;
  chain?: ChainStep[];
}

const COLUMNS: TrustColumn[] = [
  {
    title: 'Code calculates, not the LLM',
    body: 'Every dollar figure is produced by a deterministic engine DAG — fixed inputs, fixed math, reproducible output. The language model writes the prose around the number; it never invents one.',
  },
  {
    title: 'Attested',
    body: 'Nothing exports until the sign-off chain completes — each step a named human, on the record:',
    chain: [
      { role: 'Accountant', action: 'approves every engine' },
      { role: 'Attorney', action: 'Lock+Stamp on the sworn number' },
      { role: 'External CPA', action: 'finalizes the exhibit' },
    ],
  },
  {
    title: 'Traceable',
    body: 'Every figure carries its lineage — the engine that computed it and the source document it came from. Challenge any number and the platform shows its work.',
  },
];

export default function TrustLayer() {
  return (
    <section id="trust" className="border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
        <SectionHeader eyebrow="The trust layer" title="Why the number holds" />

        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {COLUMNS.map((column, index) => (
            <div key={column.title} className="border-t pt-5" style={HAIR}>
              <span className="font-mono text-[11px] text-text-dim">{String(index + 1).padStart(2, '0')}</span>
              <h3 className="mt-3 font-heading text-lg leading-snug tracking-tight text-text-primary">
                {column.title}
              </h3>
              <p className="mt-2.5 font-body text-sm leading-relaxed text-text-secondary">{column.body}</p>
              {column.chain && (
                <ol className="mt-4 border-t pt-3" style={HAIR}>
                  {column.chain.map((step) => (
                    <li
                      key={step.role}
                      className="grid grid-cols-[auto_1fr] items-baseline gap-x-2 py-1 font-mono text-[11px] text-text-muted"
                    >
                      <span className="uppercase tracking-[0.08em] text-text-secondary">{step.role} —</span>
                      <span>{step.action}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
